import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  checkOrderAlertReadiness,
  fixAllOrderAlertPermissions,
  markOrderAlertChannelConfirmed,
  openOrderAlertAppNotificationSettings,
  openOrderAlertChannelSettings,
  promptForBatteryUnrestricted,
  promptForNotifications,
  sendTestOrderAlert,
  startOrderAlertService,
  type OrderAlertReadiness,
} from '../lib/nativeOrderAlerts'
import { promptForBlePermissions } from '../lib/blePermissions'
import {
  markNativeSetupComplete,
  shouldShowOrderAlertSetup,
} from '../lib/nativeSetupGate'

type Props = {
  open: boolean
  onClose: () => void
}

export function OrderAlertSetupModal({ open, onClose }: Props) {
  const [readiness, setReadiness] = useState<OrderAlertReadiness | null>(null)
  const [busy, setBusy] = useState(false)
  const autoPromptedRef = useRef(false)

  const refresh = useCallback(async () => {
    const next = await checkOrderAlertReadiness()
    setReadiness(next)
    if (next.ready) {
      await markNativeSetupComplete()
    }
    return next
  }, [])

  useEffect(() => {
    if (!open || !Capacitor.isNativePlatform()) {
      autoPromptedRef.current = false
      return
    }
    let cancelled = false
    void (async () => {
      if (!autoPromptedRef.current) {
        autoPromptedRef.current = true
        setBusy(true)
        try {
          await promptForNotifications()
          if (cancelled) return
          await promptForBlePermissions()
        } finally {
          if (!cancelled) setBusy(false)
        }
      }
      if (!cancelled) await refresh()
    })()
    return () => {
      cancelled = true
    }
  }, [open, refresh])

  useEffect(() => {
    if (!open || !Capacitor.isNativePlatform()) return
    let cancelled = false
    let removeListener: (() => void) | undefined
    void import('@capacitor/app').then(async ({ App }) => {
      const handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && !cancelled) void refresh()
      })
      removeListener = () => void handle.remove()
    })
    return () => {
      cancelled = true
      removeListener?.()
    }
  }, [open, refresh])

  if (!open || !Capacitor.isNativePlatform()) return null

  async function handleFixAll() {
    setBusy(true)
    try {
      const next = await fixAllOrderAlertPermissions()
      setReadiness(next)
      if (next.ready) {
        await markNativeSetupComplete()
        onClose()
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleEnableNotifications() {
    setBusy(true)
    try {
      await promptForNotifications()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handleNearbyDevices() {
    setBusy(true)
    try {
      await promptForBlePermissions()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handleBattery() {
    setBusy(true)
    try {
      await promptForBatteryUnrestricted()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handleRestartListener() {
    setBusy(true)
    try {
      await startOrderAlertService()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handleOpenNotificationSettings() {
    setBusy(true)
    try {
      await openOrderAlertAppNotificationSettings()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handleOpenChannelSettings() {
    setBusy(true)
    try {
      await openOrderAlertChannelSettings()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function handlePlayTestNotification() {
    setBusy(true)
    try {
      await sendTestOrderAlert()
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirmChannelSound() {
    setBusy(true)
    try {
      markOrderAlertChannelConfirmed(true)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const notificationsOk = readiness?.notifications === 'granted'
  const bluetoothOk = readiness?.bluetooth === 'granted'
  const batteryOk = readiness?.batteryOptimized === false
  const serviceOk = readiness?.serviceRunning === true
  const channelOk = readiness?.channelSoundConfirmed === true

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal-content"
        role="dialog"
        aria-labelledby="order-alert-setup-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="order-alert-setup-title" className="modal-title">
            Enable order alerts
          </h2>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            So staff hear new customer orders when using another app, allow these settings on
            this tablet.
          </p>

          <div className="settings-field" style={{ marginTop: '1rem' }}>
            <span className="settings-label">
              Notifications {notificationsOk ? '✓' : '— needed'}
            </span>
            <p className="settings-section-desc" style={{ margin: '0.25rem 0 0.5rem' }}>
              Alert sound and banner when a new order arrives in the background.
            </p>
            {!notificationsOk && (
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => void handleEnableNotifications()}
              >
                Enable notifications
              </button>
            )}
          </div>

          <div className="settings-field">
            <span className="settings-label">
              Nearby devices {bluetoothOk ? '✓' : '— needed'}
            </span>
            <p className="settings-section-desc" style={{ margin: '0.25rem 0 0.5rem' }}>
              Bluetooth permission for receipt and label printers.
            </p>
            {!bluetoothOk && (
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => void handleNearbyDevices()}
              >
                Allow nearby devices
              </button>
            )}
          </div>

          <div className="settings-field">
            <span className="settings-label">
              Battery {batteryOk ? '✓ Unrestricted' : '— optimized'}
            </span>
            <p className="settings-section-desc" style={{ margin: '0.25rem 0 0.5rem' }}>
              Set battery to <strong>Unrestricted</strong> so the POS keeps listening when
              minimized.
            </p>
            {!batteryOk && (
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => void handleBattery()}
              >
                Open battery settings
              </button>
            )}
          </div>

          <div className="settings-field">
            <span className="settings-label">Screen stays on</span>
            <p className="settings-section-desc" style={{ margin: '0.25rem 0 0.5rem' }}>
              Screen stays on while this app is open so the register does not go dark mid-shift.
              It can sleep again when you leave the app.
            </p>
          </div>

          <div className="settings-field">
            <span className="settings-label">
              Background listener {serviceOk ? '✓ Running' : '— not running'}
            </span>
            {!serviceOk && (
              <button
                type="button"
                className="btn-ghost"
                disabled={busy}
                onClick={() => void handleRestartListener()}
                style={{ marginTop: '0.35rem' }}
              >
                Restart listener
              </button>
            )}
          </div>

          <div className="settings-field">
            <span className="settings-label">
              Notification channel sound {channelOk ? '✓ Confirmed' : '— verify now'}
            </span>
            <p className="settings-section-desc" style={{ margin: '0.25rem 0 0.5rem' }}>
              {readiness?.channelGuidance ||
                'If no sound, open notification settings and set "Order alerts" to Alerting / Sound On.'}
            </p>
            {!channelOk && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => void handleOpenNotificationSettings()}
                >
                  Open notification settings
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => void handleOpenChannelSettings()}
                >
                  Open channel settings
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => void handlePlayTestNotification()}
                >
                  Play test notification sound
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy}
                  onClick={() => void handleConfirmChannelSound()}
                >
                  I heard the chime
                </button>
              </div>
            )}
          </div>

          {readiness?.ready && (
            <p style={{ color: '#15803d', marginBottom: 0 }}>Order alerts are ready.</p>
          )}
          {readiness && !readiness.ready && readiness.issues.length > 0 && (
            <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1.25rem', color: '#92400e' }}>
              {readiness.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}>
            {readiness?.ready ? 'Done' : 'Later'}
          </button>
          {!readiness?.ready && (
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void handleFixAll()}>
              Fix all
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/** @deprecated Prefer shouldShowNativeSetup from nativeSetupGate. */
export { shouldShowOrderAlertSetup }
